/**
 * Send a wave (a group's batch key) or a single task back to the seat with
 * one note. The seat gets one `task_revision` job and revises every task in
 * place; until then the tasks stay open and sendable, marked "being revised".
 * The note survives an unmount (`useDraft`, keyed on the group or task).
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { tasksApi } from "../../api/tasks";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import { Modal } from "../Modal";
import { useToast } from "../toast/context";

export interface RequestChangesTarget {
  /** The group's batch key – every open task of the group goes back. */
  groupKey?: string;
  /** Single tasks instead of a group. */
  taskIds?: string[];
  /** What the owner sees: the group label or the task's who. */
  label: string;
  /** Open tasks that would go back. */
  count: number;
  /** The proposing seats. */
  seats: string[];
}

export function RequestChangesModal({ target, onClose }: { target: RequestChangesTarget | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = target?.groupKey ? draftKey("task-group", target.groupKey) : draftKey("task-changes", (target?.taskIds ?? []).join(","));
  const note = useDraft(key);
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!target) return;
    const text = note.draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const result = await tasksApi.requestChanges({
        note: text,
        ...(target.groupKey ? { group_key: target.groupKey } : { task_ids: target.taskIds }),
      });
      note.clearDraft();
      const sent = result.rounds.reduce((n, r) => n + r.task_ids.length, 0);
      const seats = result.rounds.map((r) => r.seat).join(", ");
      toast.ok(`${sent} ${sent === 1 ? "task goes" : "tasks go"} back to ${seats}${result.skipped.length ? ` · ${result.skipped.length} skipped` : ""}`);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["tasks"] }),
        qc.invalidateQueries({ queryKey: ["console"] }),
      ]);
      onClose();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={!!target}
      onClose={onClose}
      eyebrow="Request changes"
      title={target?.label ?? ""}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-main" disabled={busy || !note.draft.trim()} onClick={() => void send()}>
            Send back
          </button>
        </div>
      }
    >
      {target ? (
        <div className="space-y-3 text-sm">
          <p className="text-ink-medium">
            {target.count === 1 ? "This task goes" : `These ${target.count} tasks go`} back to{" "}
            <span className="font-mono">{target.seats.join(", ") || "the proposing seat"}</span> with one note. The seat
            revises every one of them; until then they stay here, marked "being revised", and you can still send them.
          </p>
          <textarea
            className="input w-full"
            rows={5}
            autoFocus
            aria-label="Your note"
            placeholder="What should change – e.g. a shorter opener, name the fund, no deck."
            value={note.draft}
            onChange={(e) => note.setDraft(e.target.value)}
          />
        </div>
      ) : null}
    </Modal>
  );
}
